import type { ComponentType } from 'react';

/**
 * Shared page-component contract every structural theme must implement.
 * See docs/architecture/ADR.md Section 16 (ADR-2026-07-B).
 *
 * Every theme package (frontend/src/themes/<key>/index.ts) must default-export an object
 * satisfying this shape. A theme missing an entry fails the build (see registry.ts) rather
 * than surfacing as a blank page / 404 in production.
 */
export interface ThemeComponentMap {
  HomePage: ComponentType;
  SearchResultsPage: ComponentType;
  PropertyDetailPage: ComponentType;
  BookingFlowPage: ComponentType;
  BookingConfirmationPage: ComponentType;
  ExperienceBrowsePage: ComponentType;
  ExperienceDetailPage: ComponentType;
  ItineraryPage: ComponentType;
  Navbar: ComponentType;
  Footer: ComponentType;
  GuestLayout: ComponentType;
  AuthLayout: ComponentType;
}

export const THEME_CONTRACT_KEYS: Array<keyof ThemeComponentMap> = [
  'HomePage',
  'SearchResultsPage',
  'PropertyDetailPage',
  'BookingFlowPage',
  'BookingConfirmationPage',
  'ExperienceBrowsePage',
  'ExperienceDetailPage',
  'ItineraryPage',
  'Navbar',
  'Footer',
  'GuestLayout',
  'AuthLayout',
];

/**
 * Pre-login auth-page contract. See docs/architecture/ADR.md Section 16 (ADR-2026-07-D).
 * Resolved via `ThemeSlot` against the platform-wide active theme, same as `ThemeComponentMap` —
 * a user's themed/fixed tier isn't knowable until after they authenticate, so auth necessarily
 * follows the platform theme rather than the eventual role.
 */
export interface AuthComponentMap {
  LoginPage: ComponentType;
  RegisterPage: ComponentType;
  ForgotPasswordPage: ComponentType;
  ResetPasswordPage: ComponentType;
}

export const AUTH_CONTRACT_KEYS: Array<keyof AuthComponentMap> = [
  'LoginPage',
  'RegisterPage',
  'ForgotPasswordPage',
  'ResetPasswordPage',
];

export interface ImageLightboxProps {
  images: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  bookingId: string;
}

export interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  bookingId: string;
}

export interface ReviewSubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment: string) => void;
  targetName: string;
}

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
}

export interface PropertyViewEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  mode: 'view' | 'edit';
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

export interface ToastThemeTokens {
  style: {
    background: string;
    color: string;
    borderRadius: string;
    border?: string;
  };
  success: { iconTheme: { primary: string; secondary: string } };
  error: { iconTheme: { primary: string; secondary: string } };
}

/**
 * Themed-tier dashboard contract (Guest, Org Admin, Host, Property Manager, Staff). See
 * docs/architecture/ADR.md Section 16 (ADR-2026-07-D). Fixed-tier roles (Super Admin, Platform
 * Admin, Platform Staff) never resolve against this map — see `roleTier.ts`/`DashboardThemeSlot`.
 */
export interface DashboardComponentMap {
  /** Renders its own `<Outlet />` internally, same pattern as the pre-v2.3 `DashboardLayout`. */
  DashboardShell: ComponentType;
  // Dashboard "index" / landing pages, one per role group
  GuestOverviewPage: ComponentType;
  /** Shared landing page concept across Org Admin / Host / Property Manager (all "property-related"). */
  PropertyOverviewPage: ComponentType;
  StaffOverviewPage: ComponentType;
  // Guest
  MyBookingsPage: ComponentType;
  MyTripsPage: ComponentType;
  TripDetailPage: ComponentType;
  FavoritesPage: ComponentType;
  GuestMessagesPage: ComponentType;
  GuestReviewsPage: ComponentType;
  // Property-related (Org Admin / Host / Property Manager, gated by existing role menu config)
  PropertiesManagementPage: ComponentType;
  PropertyCreateEditPage: ComponentType;
  ExperienceCreatePage: ComponentType;
  /** ORG_ADMIN only -- invite/list team members within their own org. */
  TeamManagementPage: ComponentType;
  BookingsManagementPage: ComponentType;
  GuestsDirectoryPage: ComponentType;
  AnalyticsPage: ComponentType;
  CommissionPage: ComponentType;
  PayoutSettingsPage: ComponentType;
  CalendarSyncPage: ComponentType;
  ChannelSyncPage: ComponentType;
  ReviewsManagementPage: ComponentType;
  PoliciesPage: ComponentType;
  AuditLogPage: ComponentType;
  CompliancePage: ComponentType;
  HostMessagesPage: ComponentType;
  // Staff
  StaffTasksPage: ComponentType;
  StaffSchedulePage: ComponentType;
  // Shared across all themed-tier roles
  ProfileSettingsPage: ComponentType;
  // Modals/popups
  ImageLightbox: ComponentType<ImageLightboxProps>;
  CancellationModal: ComponentType<CancellationModalProps>;
  RejectModal: ComponentType<RejectModalProps>;
  ReviewSubmitModal: ComponentType<ReviewSubmitModalProps>;
  ShareModal: ComponentType<ShareModalProps>;
  PropertyViewEditModal: ComponentType<PropertyViewEditModalProps>;
  ConfirmDialog: ComponentType<ConfirmDialogProps>;
  toastTheme: ToastThemeTokens;
}

export const DASHBOARD_CONTRACT_KEYS: Array<keyof DashboardComponentMap> = [
  'DashboardShell',
  'GuestOverviewPage',
  'PropertyOverviewPage',
  'StaffOverviewPage',
  'MyBookingsPage',
  'MyTripsPage',
  'TripDetailPage',
  'FavoritesPage',
  'GuestMessagesPage',
  'GuestReviewsPage',
  'PropertiesManagementPage',
  'PropertyCreateEditPage',
  'ExperienceCreatePage',
  'TeamManagementPage',
  'BookingsManagementPage',
  'GuestsDirectoryPage',
  'AnalyticsPage',
  'CommissionPage',
  'PayoutSettingsPage',
  'CalendarSyncPage',
  'ChannelSyncPage',
  'ReviewsManagementPage',
  'PoliciesPage',
  'AuditLogPage',
  'CompliancePage',
  'HostMessagesPage',
  'StaffTasksPage',
  'StaffSchedulePage',
  'ProfileSettingsPage',
  'ImageLightbox',
  'CancellationModal',
  'RejectModal',
  'ReviewSubmitModal',
  'ShareModal',
  'PropertyViewEditModal',
  'ConfirmDialog',
  'toastTheme',
];

/** A full theme package: the three contracts a theme must satisfy (ADR Section 16). */
export interface ThemePackage {
  pages: ThemeComponentMap;
  auth: AuthComponentMap;
  dashboard: DashboardComponentMap;
}

/** Runtime check backing THEME-001/THEME-011's "contract completeness" guarantee. */
function assertMapComplete<T extends object>(
  themeKey: string,
  scope: string,
  map: Partial<T>,
  keys: Array<keyof T>,
): void {
  const missing = keys.filter((key) => !map[key]);
  if (missing.length > 0) {
    throw new Error(
      `Theme "${themeKey}" is missing required ${scope} component(s): ${missing.join(', ')}`,
    );
  }
}

export function assertThemeComplete(themeKey: string, pkg: Partial<ThemePackage>): void {
  assertMapComplete(themeKey, 'page', pkg.pages ?? {}, THEME_CONTRACT_KEYS);
  assertMapComplete(themeKey, 'auth', pkg.auth ?? {}, AUTH_CONTRACT_KEYS);
  assertMapComplete(themeKey, 'dashboard', pkg.dashboard ?? {}, DASHBOARD_CONTRACT_KEYS);
}
